import {
  Color3,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  Scene,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core';
import type { BollardPlacement } from '@roadworld/world';

const BOLLARD_HEIGHT = 0.85;
const BOLLARD_DIAMETER = 0.18;
const BOLLARD_TESSELLATION = 6;

/**
 * Per-chunk batch of roadside bollards. Single base cylinder, thin-instanced. Owns the base
 * mesh — call dispose() on chunk unload.
 */
export interface BollardBatch {
  /** Show/hide the whole batch. Used by the chunk LOD system to drop bollards in the
   *  cached ring (they're fine detail not worth rendering at distance). */
  setEnabled(on: boolean): void;
  dispose(): void;
}

export function buildBollardBatch(
  scene: Scene,
  key: string,
  placements: readonly BollardPlacement[],
  rootOffset: Vector3,
): BollardBatch | null {
  if (placements.length === 0) return null;

  const base = MeshBuilder.CreateCylinder(
    `bollard-base-${key}`,
    {
      diameter: BOLLARD_DIAMETER,
      height: BOLLARD_HEIGHT,
      tessellation: BOLLARD_TESSELLATION,
    },
    scene,
  );
  const mat = new StandardMaterial(`bollard-mat-${key}`, scene);
  mat.diffuseColor = new Color3(0.85, 0.85, 0.88);
  mat.specularColor = new Color3(0.1, 0.1, 0.1);
  base.material = mat;

  const matrices = new Float32Array(placements.length * 16);
  const tmpQ = new Quaternion();
  const tmpM = new Matrix();
  const halfHeight = new Vector3(0, BOLLARD_HEIGHT * 0.5, 0);

  for (let i = 0; i < placements.length; i++) {
    const p = placements[i]!;
    Quaternion.RotationAxisToRef(Vector3.UpReadOnly, p.yaw, tmpQ);
    const pos = new Vector3(p.x + rootOffset.x, p.y, p.z + rootOffset.z).add(halfHeight);
    Matrix.ComposeToRef(new Vector3(1, 1, 1), tmpQ, pos, tmpM);
    tmpM.copyToArray(matrices, i * 16);
  }

  base.thinInstanceSetBuffer('matrix', matrices, 16, false);
  base.alwaysSelectAsActiveMesh = true;

  return {
    setEnabled(on: boolean): void {
      base.setEnabled(on);
    },
    dispose(): void {
      base.dispose(false, true);
    },
  };
}
